/**
 * The routes the `app` zone worker (app.lokerdollar.com) actually serves.
 *
 * This list is load-bearing in TWO places that point at each other:
 *
 *  - `src/middleware.ts` redirects apex `/{locale}/<one of these>` TO the zone.
 *  - `apps/app/src/middleware.ts` bounces anything NOT in this list back to the
 *    apex, because it 404s on that subdomain.
 *
 * They were separate copies of the same regex until 2026-09-09. A drift
 * between them is a redirect loop by construction — a path in the apex's copy
 * but not the zone's is sent over and bounced straight back, forever. Keeping
 * one exported list is what makes that class of drift unrepresentable, rather
 * than merely checked for by a guard that has to be remembered.
 *
 * Adding a route here is a two-worker change: the page must exist under
 * `apps/app/src/app/[locale]/(app)/` before the apex starts redirecting to it,
 * or the zone will bounce it right back.
 */
export const APP_ZONE_PATHS = [
	"seeker",
	"applications",
	"billing",
	"resume",
	"saved",
	"settings",
	"upgrade",
	"onboarding",
] as const;

/** The apex worker's origin — /login, /signup, /jobs, /pricing live here. */
export const APEX_ORIGIN = "https://lokerdollar.com";

/** The seeker zone worker's origin. */
export const APP_ZONE_ORIGIN = "https://app.lokerdollar.com";

const LOCALE_SEGMENT = "(?:id|en)";
const APP_PATH_GROUP = APP_ZONE_PATHS.join("|");

/**
 * `/{locale}/{app route}` with an optional subpath — the shape the apex hands
 * to the zone and the shape the zone agrees to serve.
 */
const APP_ZONE_PATH_RE = new RegExp(
	`^/${LOCALE_SEGMENT}/(?:${APP_PATH_GROUP})(?:/.*)?$`,
);

/** Bare `/{locale}` — the zone's own home, which it serves rather than bounces. */
const BARE_LOCALE_ROOT_RE = new RegExp(`^/${LOCALE_SEGMENT}/?$`);

export function isAppZonePath(pathname: string): boolean {
	return APP_ZONE_PATH_RE.test(pathname);
}

export function isBareLocaleRoot(pathname: string): boolean {
	return BARE_LOCALE_ROOT_RE.test(pathname);
}

/**
 * True for a path the `app` zone worker serves itself — i.e. one it must NOT
 * bounce to the apex.
 */
export function isServedByAppZone(pathname: string): boolean {
	return isAppZonePath(pathname) || isBareLocaleRoot(pathname);
}
